import { ArrowUpRight, Check } from "lucide-react";
import { DealScoreGauge } from "../DealScoreGauge";
import { PropertyImage } from "../PropertyImage";
import { MetricRow } from "./primitives";
import { fmtMoney, fmtSigned, fmtPct, dealProperty } from "./format";

/** Homepage deal card. Values come straight from /api/top-deals; nothing is recomputed. */
export function PropertyDealCard({ deal, onOpen, testId }) {
  const p = dealProperty(deal);
  const cf = deal?.cash_flow_monthly;
  const coc = deal?.cash_on_cash ?? deal?.cash_on_cash_return;
  const address = p.address || "Address unavailable";
  const location = [p.city, p.state].filter(Boolean).join(", ");

  return (
    <article
      className="rd-card rd-card-hover overflow-hidden min-w-0 flex flex-col cursor-pointer group focus-within:ring-2 focus-within:ring-rd-action"
      onClick={onOpen}
      data-testid={testId}
    >
      <div className="relative h-44 bg-rd-navy">
        <PropertyImage
          property={p}
          alt={`${address}${location ? `, ${location}` : ""} — listing photo`}
          className="h-44"
          imgClassName="w-full h-44 object-cover"
          allowZoom={false}
        />
        {p.property_type && (
          <span className="absolute top-3 left-3 text-[11px] font-bold uppercase tracking-wider px-2 py-1 rounded-md rd-nav-bg text-white">
            {String(p.property_type).replace(/_/g, " ")}
          </span>
        )}
      </div>

      <div className="p-5 flex flex-col gap-3 flex-1">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="rd-h3 text-base sm:text-lg text-rd-text truncate" title={address}>{address}</h3>
            <p className="text-sm text-rd-text-2 truncate">{location}{p.zip_code ? ` ${p.zip_code}` : ""}</p>
            <p className="mt-2 font-mono font-bold text-2xl text-rd-text tabular-nums">{fmtMoney(p.price)}</p>
          </div>
          <DealScoreGauge score={deal?.deal_score} size="sm" />
        </div>

        <div className="rounded-xl bg-rd-offwhite border border-rd-border px-4">
          <MetricRow label="Estimated Rent" value={deal?.estimated_monthly_rent != null ? `${fmtMoney(deal.estimated_monthly_rent)}/mo` : "—"} />
          <MetricRow label="Cash Flow" value={cf != null ? `${fmtSigned(cf)}/mo` : "—"} tone={cf == null ? "neutral" : cf >= 0 ? "positive" : "negative"} emphasis />
          <MetricRow label="Cap Rate" value={fmtPct(deal?.cap_rate)} />
          {coc != null && <MetricRow label="Cash-on-Cash ROI" value={fmtPct(coc)} />}
        </div>

        <div className="mt-auto -mb-1 flex items-center justify-between text-sm">
          {deal?.one_percent_rule ? (
            <span className="inline-flex items-center gap-1.5 text-rd-text-2"><Check className="w-4 h-4 text-rd-green" aria-hidden="true" /> Passes 1% rule</span>
          ) : <span />}
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onOpen?.(); }}
            className="rd-link inline-flex items-center gap-1 min-h-[44px]"
            aria-label={`View analysis for ${address}`}
          >
            View analysis <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" aria-hidden="true" />
          </button>
        </div>
      </div>
    </article>
  );
}

export function PropertyDealCardSkeleton() {
  return (
    <div className="rd-card overflow-hidden" aria-hidden="true">
      <div className="rd-skeleton h-44 rounded-none" />
      <div className="p-5 space-y-3">
        <div className="rd-skeleton h-5 w-3/4" />
        <div className="rd-skeleton h-4 w-1/2" />
        <div className="rd-skeleton h-8 w-1/3" />
        <div className="rd-skeleton h-28 w-full" />
      </div>
    </div>
  );
}
